<script setup lang="ts">
import SingleResult from "./SingleResult.vue";
import { computed, onMounted, reactive } from "vue";
import Modal from "../components/Modal.vue";
import QuoteSummary from "../components/QuoteSummary.vue";
import { useRoute } from "vue-router";
import { useQuoteStore } from "../stores/quotes";
import { ensureDefined } from "../utils/types";
import ToastRenderer from "../components/ToastRenderer.vue";
import { parseQueryQuoteNumber } from "@/utils";
import { sortedPremiums } from "@/utils/quote";
import { useGrowthBookFeatures } from "@/composables/growthbook";
import Premium from "./Results/Premium/Premium.vue";

const route = useRoute();
const quoteStore = useQuoteStore();
const { redesign_lg_indicative_results202605 } = useGrowthBookFeatures();

export type Results = {
    showQuoteSummaryModal: boolean;
};

const state: Results = reactive({
    showQuoteSummaryModal: false,
});

const quote = computed(() => ensureDefined(quoteStore.quote));

const quoteNumber = computed(() => parseQueryQuoteNumber(route.query?.quotenumber) ?? "");

onMounted(() => {
    const token = quoteStore.getLatestQuoteToken();
    quoteStore.getQuote(token);
    quoteStore.addScottishWidowsPremiumToResults();
});

</script>

<template>
    <form id="resultsForm">
        <div class="row mt-3 justify-content-between">
            <div class="">
                <button
                    id="btn-quote-summary"
                    data-toggle="modal"
                    data-target="#oldQuotes"
                    type="button"
                    class="mb-2 btn btn-secondary"
                    @click="state.showQuoteSummaryModal = true"
                >
                    <span aria-hidden="true">Quote summary</span>
                </button>
            </div>
        </div>
        <div class="row">
            <div id="parameters">
                <div id="results">
                    <div id="accordion">
                        <div
                            v-if="quoteStore.quote"
                            class="row"
                        >
                            <template
                                v-for="quotation in sortedPremiums(
                                    quoteStore.quotations, 3
                                )"
                                :key="`${quotation.premiumId}-${quotation.company.slug}`"
                            >
                                <Premium
                                    v-if="redesign_lg_indicative_results202605"
                                    :premium="quotation"
                                    :payment-frequency="quoteStore.quote.product.paymentFrequency"
                                    :quote-number="quoteNumber"
                                />
                                <SingleResult
                                    v-else
                                    :id="'result_' + quoteNumber"
                                    :quotation="quotation"
                                    :quote="quote"
                                    :quote-number="quoteNumber"
                                    :is-open="false"
                                    :show-more-info-button="false"
                                />
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
    <Modal
        id="quote-summary-modal"
        open-focus="close-quote-summary-modal"
        close-focus="btn-quote-summary"
        header="Your quote summary"
        :show="state.showQuoteSummaryModal"
        @close="state.showQuoteSummaryModal = false"
    >
        <template #body>
            <QuoteSummary :quote="quote" />
        </template>
    </Modal>
    <ToastRenderer />
</template>
<style scoped>
.get-quotes-bar {
    position: relative;
    z-index: 2;
}
</style>
